import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  chantVarsLink,
  composeSankalpa,
  DEITIES,
  GOTRA_PRESETS,
  KAMANAS,
  KARMAS,
  SANKALPA_DEITY_KEYS,
  SANKALPA_KAMANA_KEYS,
  SANKALPA_KARMA_KEYS,
  SANKALPA_LEVELS,
  SankalpaPreferences as SankalpaPreferencesSchema,
  TRADITIONS,
  type SankalpaDeity,
  type SankalpaKamana,
  type SankalpaKarma,
  type SankalpaLevel,
  type SankalpaOptions,
  type SankalpaPreferences,
  type SankalpaTradition,
} from "@vedaunion/shared";
import ChantReader from "../chant/ChantReader";
import { LocationPicker, useLocationPref } from "../calendar/LocationPicker";
import { useSankalpaCoordinates, usePanchangStatus } from "../../lib/panchang";
import { usePreferences } from "../../lib/preferences";
import { useChantVars } from "../../lib/chant-vars";
import { useMe } from "../../lib/auth";

/**
 * The saṅkalpa — ONE implementation, two mounts.
 *
 * Standalone at `/sankalpa`, and embedded in any Library document (Pūjā Vidhi
 * step 3) through the `sankalpa` block. It is the first VARIABLE module: it
 * composes a marked document at render time from the day's pañcāṅga plus the
 * reader's own choices, and hands it to the same `ChantReader` that renders
 * every static chant — so it looks identical to every other Veda Union text.
 *
 * STATE. Every choice lives in `preferences.sankalpa` (the DB for a signed-in
 * user, localStorage for a guest — `usePreferences` owns that split), so the
 * document does not own a private copy and a choice made here is still there on
 * the next visit and on the other mount. The `deity` in particular is the
 * DOCUMENT-LEVEL deity: the Pūjā Vidhi reader fills its `deity` slots from the
 * same value, so one choice re-voices the whole rite.
 *
 * `level` is the exception: an embedded instance may pin a starting level
 * (Pūjā Vidhi opens at `simple`, which is what the class teaches at that step)
 * and then keeps the level in local state, so raising it to the full or mahā
 * form in place doesn't rewrite the reader's own default.
 *
 * VARIABLES BY URL. `?v.deity=ganesha&v.name=…` renders this saṅkalpa the way
 * its sender saw it, so a link can be shared and mean the same thing at the
 * other end (`shared/src/chant-vars.ts`). Precedence is URL > saved preference
 * > default, and — the half that matters — a URL value applies to THIS VIEW
 * ONLY. It is never written into the recipient's preferences: opening someone
 * else's saṅkalpa must not rewrite your own deity or your own gotra. The
 * override drops away the moment the recipient changes that control themselves,
 * and only then is anything saved, because that choice is theirs.
 */

export interface SankalpaModuleProps {
  /** Start at this level and keep it local, instead of using the saved one. */
  levelOverride?: SankalpaLevel;
  /** Render inside a document (no page chrome, tighter controls). */
  embedded?: boolean;
  /** Heading shown above the controls. Omit for none. */
  heading?: string;
}

const gotraLabel = (g: { label: string; note?: string }) =>
  g.note ? `${g.label} — ${g.note}` : g.label;

export function SankalpaModule({ levelOverride, embedded = false, heading }: SankalpaModuleProps) {
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const [date, setDate] = useState(todayStr);
  const [refineOpen, setRefineOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const { prefs, update } = usePreferences();
  // View-only overrides from `?v.*`. Validated field by field against the same
  // schema the stored preferences use, so a junk parameter is ignored rather
  // than rendering a broken saṅkalpa — and NEVER passed to `update`.
  const { vars: urlVars, release } = useChantVars();
  const view = useMemo(() => {
    const out: Partial<SankalpaPreferences> = {};
    for (const [k, raw] of Object.entries(urlVars)) {
      const r = SankalpaPreferencesSchema.partial().safeParse({ [k]: raw });
      if (!r.success) continue;
      const v = (r.data as Record<string, unknown>)[k];
      // Every field carries `.catch()`, so an INVALID value parses to the
      // field's default rather than failing — which would quietly override the
      // reader's own choice with a default they never picked. A value that
      // changed under parsing (or an unknown key, stripped to undefined) is
      // therefore treated as invalid and dropped.
      if (v === raw) (out as Record<string, unknown>)[k] = v;
    }
    return out;
  }, [urlVars]);
  const sk = useMemo(() => ({ ...prefs.sankalpa, ...view }), [prefs.sankalpa, view]);
  const me = useMe();
  const user = me.data?.user ?? null;
  const statusQ = usePanchangStatus();
  const { location, setLocation } = useLocationPref();

  // An embedded instance pins its starting level; the standalone page follows
  // the saved preference.
  const [localLevel, setLocalLevel] = useState<SankalpaLevel | null>(levelOverride ?? null);
  const level: SankalpaLevel = localLevel ?? sk.level;
  const setLevel = (v: SankalpaLevel) => {
    release("level");
    if (levelOverride) setLocalLevel(v);
    else update({ sankalpa: { level: v } });
  };

  // A control the RECIPIENT touches is theirs: the URL override for that one
  // field is released, and only then does the value persist.
  const set = <K extends keyof SankalpaPreferences>(k: K, v: SankalpaPreferences[K]) => {
    release(k as string);
    // Picking the deity HERE is just as much a choice as picking it in the
    // reader's own settings, and the pūjā gates its per-deity verses on the act
    // of choosing (AUTHORING-CHANTS §5E rule 3). Without this, choosing a deity
    // on the saṅkalpa left the rite reading `śrī devaṁ`.
    const extra = k === "deity" ? { deityChosen: true } : null;
    update({ sankalpa: { [k]: v, ...extra } as Partial<SankalpaPreferences> });
  };

  // Seed the performer's details from the account once, if they're still empty.
  useEffect(() => {
    if (!user) return;
    // A link that carries a name or a gotra is showing you the sender's; do not
    // overwrite the stored value underneath it while it is on screen.
    if (urlVars.name || urlVars.gotra) return;
    const patch: Partial<SankalpaPreferences> = {};
    if (!sk.name && user.displayName) patch.name = user.displayName;
    if (user.gotra && sk.gotra === "kāśyapa") patch.gotra = user.gotra;
    if (Object.keys(patch).length) update({ sankalpa: patch });
    // once per user identity — not on every preference change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // The `simple` level is entirely offline — no pañcāṅga needed, so no request.
  const needsCoords = level !== "simple";
  const coordsQ = useSankalpaCoordinates(date, needsCoords, location);
  const rawCoords = coordsQ.data?.coordinates ?? null;
  // `v.place` names the place in the sender's own saṅkalpa. It replaces only
  // the NAME the deśa line prints; the pañcāṅga still belongs to the reader's
  // own location, because a tithi computed elsewhere would be a false one.
  const coords = useMemo(
    () => (rawCoords && urlVars.place ? { ...rawCoords, placeName: urlVars.place } : rawCoords),
    [rawCoords, urlVars.place],
  );

  const opts: SankalpaOptions = useMemo(
    () => ({
      level,
      tradition: sk.tradition,
      karma: sk.karma,
      kamana: sk.kamana,
      deity: sk.deity,
      placeFrame: sk.placeFrame,
      gotra: sk.gotra,
      name: sk.name,
    }),
    [level, sk.tradition, sk.karma, sk.kamana, sk.deity, sk.placeFrame, sk.gotra, sk.name],
  );

  const composed = useMemo(() => composeSankalpa(coords, opts), [coords, opts]);

  const script = prefs.chant.primaryScript;
  const copyText = composed.lines
    .map((l) => (script === "iast" ? l.iast : l.deva))
    .join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — nothing useful to say */
    }
  };

  // The link that reproduces THIS view. Every variable is serialised, whether
  // it came from a preference or from the incoming URL, so the recipient sees
  // what the sender sees — including an empty name, which simply stays empty
  // and renders as its placeholder.
  const [linked, setLinked] = useState(false);
  const shareLink = () => {
    if (typeof window === "undefined") return "";
    return new URL(
      chantVarsLink(window.location.pathname + window.location.search + window.location.hash, {
        level, tradition: sk.tradition, karma: sk.karma, kamana: sk.kamana,
        deity: sk.deity, placeFrame: sk.placeFrame, gotra: sk.gotra, name: sk.name,
        place: urlVars.place ?? (sk.placeFrame === "adapted" ? placeName : ""),
      }),
      window.location.origin,
    ).toString();
  };
  const copyLink = async () => {
    const url = shareLink();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setLinked(true);
      setTimeout(() => setLinked(false), 1800);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const placeName = location?.place ?? statusQ.data?.locationName ?? "Krnjak";
  const waiting = needsCoords && coordsQ.isLoading && !coords;

  return (
    <section className={`sk-module${embedded ? " sk-module--embedded" : ""}`}>
      {heading ? <h3 className="sk-module__heading">{heading}</h3> : null}

      {/* Level — the one control that changes how much you say */}
      <div className="sk-levels" role="tablist" aria-label="Saṅkalpa length">
        {SANKALPA_LEVELS.map((l) => (
          <button
            key={l.key}
            type="button"
            role="tab"
            aria-selected={level === l.key}
            className={`sk-level${level === l.key ? " is-on" : ""}`}
            onClick={() => setLevel(l.key)}
            title={l.blurb}
          >
            {l.label}
          </button>
        ))}
      </div>

      <div className="sk-settings">
        <label className="sk-mini">
          <span className="sk-mini-label">Deity</span>
          <select
            className="sk-mini-input"
            value={sk.deity}
            onChange={(e) => set("deity", e.target.value as SankalpaDeity)}
          >
            {SANKALPA_DEITY_KEYS.map((k) => (
              <option key={k} value={k}>{DEITIES[k].label}</option>
            ))}
          </select>
        </label>
        <label className="sk-mini">
          <span className="sk-mini-label">Rite</span>
          <select
            className="sk-mini-input"
            value={sk.karma}
            onChange={(e) => set("karma", e.target.value as SankalpaKarma)}
          >
            {SANKALPA_KARMA_KEYS.map((k) => (
              <option key={k} value={k}>{KARMAS[k].label}</option>
            ))}
          </select>
        </label>
        {level !== "simple" ? (
          <>
            <label className="sk-mini">
              <span className="sk-mini-label">Intention</span>
              <select
                className="sk-mini-input"
                value={sk.kamana}
                onChange={(e) => set("kamana", e.target.value as SankalpaKamana)}
              >
                {SANKALPA_KAMANA_KEYS.map((k) => (
                  <option key={k} value={k}>{KAMANAS[k].label}</option>
                ))}
              </select>
            </label>
            <label className="sk-mini">
              <span className="sk-mini-label">Tradition</span>
              <select
                className="sk-mini-input"
                value={sk.tradition}
                onChange={(e) => set("tradition", e.target.value as SankalpaTradition)}
              >
                {Object.entries(TRADITIONS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </label>
            <label className="sk-mini">
              <span className="sk-mini-label">Day</span>
              <input
                type="date"
                className="sk-mini-input"
                value={date}
                onChange={(e) => setDate(e.target.value || todayStr)}
              />
            </label>
            <div className="sk-mini">
              <span className="sk-mini-label">Location</span>
              <LocationPicker location={location} onLocationChange={setLocation} />
            </div>
          </>
        ) : null}
      </div>

      {level !== "simple" ? (
        <>
          <button
            type="button"
            className="sk-advanced-toggle"
            onClick={() => setRefineOpen((v) => !v)}
            aria-expanded={refineOpen}
          >
            {refineOpen ? "− " : "+ "}Refine — place, your details
          </button>
          {refineOpen ? (
            <div className="sk-refine">
              <label className="sk-check">
                <input
                  type="checkbox"
                  checked={sk.placeFrame === "adapted"}
                  onChange={(e) => set("placeFrame", e.target.checked ? "adapted" : "bharata")}
                />
                <span>Name my actual location (uncheck for the classical Bhārata frame)</span>
              </label>
              <div className="sk-row">
                <label className="sk-field">
                  <span className="sk-label">Gotra</span>
                  <select
                    className="sk-input"
                    value={GOTRA_PRESETS.some((g) => g.value === sk.gotra) ? sk.gotra : "other"}
                    onChange={(e) => set("gotra", e.target.value === "other" ? "" : e.target.value)}
                  >
                    {GOTRA_PRESETS.map((g) => (
                      <option key={g.value} value={g.value}>{gotraLabel(g)}</option>
                    ))}
                    <option value="other">Other…</option>
                  </select>
                  {!GOTRA_PRESETS.some((g) => g.value === sk.gotra) ? (
                    <input
                      type="text"
                      className="sk-input"
                      placeholder="your gotra (IAST)"
                      value={sk.gotra}
                      onChange={(e) => set("gotra", e.target.value)}
                      style={{ marginTop: "0.4rem" }}
                    />
                  ) : null}
                </label>
                <label className="sk-field">
                  <span className="sk-label">Name</span>
                  <input
                    type="text"
                    className="sk-input"
                    placeholder="your name"
                    value={sk.name}
                    onChange={(e) => set("name", e.target.value)}
                  />
                </label>
              </div>
              <p className="sk-note">
                Gotra is patrilineal (passed down the father’s line from a ṛṣi) — it
                cannot be worked out from a birth date. If you don’t know yours, the
                traditional default is <em>kāśyapa</em>.
              </p>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="sk-stage" aria-live="polite">
        {waiting ? <p className="sk-muted">Computing the pañcāṅga…</p> : null}
        {needsCoords && coordsQ.isError ? (
          <p className="sk-muted">
            The pañcāṅga could not be computed for this day — showing the short form.
          </p>
        ) : null}
        <ChantReader doc={composed.doc} embedded showHero={false} bareControls />
      </div>

      <div className="sk-foot-row">
        {coords ? (
          <p className="sk-foot">
            {placeName} · {format(new Date(`${date}T00:00:00`), "EEEE, d MMMM yyyy")}
          </p>
        ) : <span />}
        <span className="sk-foot-actions">
          <button type="button" className="sk-copy" onClick={copyLink}>
            {linked ? "Link copied" : "Copy link"}
          </button>
          <button type="button" className="sk-copy" onClick={copy}>
            {copied ? "Copied" : "Copy"}
          </button>
        </span>
      </div>
    </section>
  );
}

export default SankalpaModule;
